import { useMemo } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { EspelhoLegenda } from "@/components/escala/EspelhoLegenda";
import { porNome, useRelatorio } from "@/components/escala/useRelatorio";
import { colunasDoMes } from "@/components/escala/espelho";
import { formatNumero, formatPercentual, plural } from "@/components/escala/relatorio";
import { estilosDoTema, rotuloDoTipo, type EstiloTipo } from "@/components/escala/tiposDia";
import { formatDataBR, relDomingos, rotuloLoja, type LinhaDomingo } from "@/api/escala.service";

const ROTULO_SEXO = { F: "Feminino", M: "Masculino" } as const;

// Quem trocou de loja no mês tem uma linha por loja.
const chaveLinha = (l: LinhaDomingo) => `${l.colaborador_id}|${l.loja_id}`;

// Domingos trabalhados em sequência por colaboradora, o que o revezamento do art. 386 não permite.
function seguidosDaMulher(l: LinhaDomingo, domingos: string[]): Set<string> {
  const seguidos = new Set<string>();
  if (l.sexo !== "F") return seguidos;
  const tipos = new Map(l.domingos.map((d) => [d.data, d.tipo]));
  domingos.forEach((data, i) => {
    if (i > 0 && tipos.get(data) === "T" && tipos.get(domingos[i - 1]!) === "T") seguidos.add(data);
  });
  return seguidos;
}

function SeloDomingo({ data, tipo, estilo, seguido }: { data: string; tipo?: string; estilo?: EstiloTipo; seguido: boolean }) {
  const dia = data.slice(8, 10);
  return (
    <Box
      title={`${formatDataBR(data)} — ${tipo ? rotuloDoTipo(tipo) : "Sem escala"}${seguido ? " — domingo seguido (art. 386)" : ""}`}
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        lineHeight: 1,
        bgcolor: estilo?.bg ?? "transparent",
        color: estilo?.fg ?? "text.disabled",
        border: estilo ? "none" : "1px dashed",
        borderColor: "divider",
        ...(seguido && { outline: "2px solid", outlineColor: "warning.main", outlineOffset: 1 }),
      }}
    >
      <Box sx={{ fontSize: 11, fontWeight: estilo?.peso ?? 500 }}>{tipo ?? "–"}</Box>
      <Box sx={{ fontSize: 9, opacity: 0.85 }}>{dia}</Box>
    </Box>
  );
}

function indicadoresDomingos(linhas: LinhaDomingo[], domingos: string[], seguidos: number): [Indicador, Indicador, Indicador, Indicador] {
  const mulheres = linhas.filter((l) => l.sexo === "F").length;
  const homens = linhas.filter((l) => l.sexo === "M").length;
  const trabalharam = linhas.filter((l) => l.domingos_trabalhados > 0).length;
  const totalTrabalhados = linhas.reduce((acc, l) => acc + l.domingos_trabalhados, 0);

  return [
    { titulo: "Colaboradores", valor: linhas.length, nota: `${plural(mulheres, "mulher", "mulheres")} · ${plural(homens, "homem", "homens")}` },
    {
      titulo: "Trabalharam algum domingo",
      valor: trabalharam,
      nota: linhas.length ? `${formatPercentual(trabalharam / linhas.length)} da equipe` : "nenhum colaborador no filtro",
    },
    {
      titulo: "Média por colaborador",
      valor: linhas.length ? formatNumero(totalTrabalhados / linhas.length) : "—",
      nota: `de ${plural(domingos.length, "domingo")}: ${domingos.map((d) => d.slice(8, 10)).join(", ")}`,
    },
    {
      titulo: "Mulheres em domingos seguidos",
      valor: seguidos,
      nota: "revezamento do art. 386 da CLT",
      tom: seguidos ? "critico" : "bom",
    },
  ];
}

// Cada domingo do mês por colaborador — o revezamento aparece na sequência, não só na contagem.
export function EscalaRelDomingosTab() {
  const theme = useTheme();
  const r = useRelatorio({ buscar: relDomingos, ordenar: porNome, mensagemErro: "Erro ao carregar o relatório de domingos trabalhados" });

  const estilos = useMemo(() => estilosDoTema(theme), [theme]);
  const domingos = useMemo(
    () => colunasDoMes(r.filtros.ano, r.filtros.mes).filter((c) => c.domingo).map((c) => c.iso),
    [r.filtros.ano, r.filtros.mes],
  );
  const seguidos = useMemo(() => new Map(r.linhas.map((l) => [chaveLinha(l), seguidosDaMulher(l, domingos)])), [r.linhas, domingos]);
  const totalSeguidos = useMemo(() => [...seguidos.values()].reduce((acc, s) => acc + s.size, 0), [seguidos]);
  const indicadores = useMemo(() => indicadoresDomingos(r.linhas, domingos, totalSeguidos), [r.linhas, domingos, totalSeguidos]);

  const domingosExportados = (l: LinhaDomingo) => l.domingos.map((d) => `${formatDataBR(d.data)} ${rotuloDoTipo(d.tipo)}`).join(" · ");
  const sexoLabel = (l: LinhaDomingo) => (l.sexo ? ROTULO_SEXO[l.sexo] : "");

  const columns: Column<LinhaDomingo>[] = [
    { key: "matricula", label: "Matrícula" },
    { key: "nome", label: "Nome" },
    { key: "sexo", label: "Sexo", render: sexoLabel, exportValue: sexoLabel },
    { key: "cargo", label: "Cargo" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    {
      key: "domingos",
      label: "Domingos do Mês",
      render: (l) => {
        const tipos = new Map(l.domingos.map((d) => [d.data, d.tipo]));
        const destaque = seguidos.get(chaveLinha(l));
        return (
          <Stack direction="row" spacing={0.75}>
            {domingos.map((data) => {
              const tipo = tipos.get(data);
              return <SeloDomingo key={data} data={data} tipo={tipo || undefined} estilo={tipo ? estilos[tipo] : undefined} seguido={!!destaque?.has(data)} />;
            })}
          </Stack>
        );
      },
      exportValue: domingosExportados,
    },
    {
      key: "domingos_trabalhados",
      label: "Trabalhados",
      render: (l) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {l.domingos_trabalhados}
          <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
            {" "}
            de {domingos.length}
          </Box>
        </Box>
      ),
      exportValue: (l) => l.domingos_trabalhados,
    },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "domingos-trabalhados", aba: "Domingos" }}
      competencia={r.filtros}
      legenda={<EspelhoLegenda feriado={false} />}
    >
      <DataTable columns={columns} {...r.tabela} />
    </RelatorioLayout>
  );
}
